import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Edit, Target, Flag, Palette, ChevronDown, ChevronRight, Calendar, User, X } from 'lucide-react';
import api from '../api';
import { Breadcrumbs } from './Breadcrumbs';

const TAB_CONFIG = [
  { key: 'goals', label: 'Goals', icon: Target, description: 'Strategic goals that drive your roadmap' },
  { key: 'okrs', label: 'OKRs', icon: Flag, description: 'Objectives & Key Results' },
  { key: 'themes', label: 'Themes', icon: Palette, description: 'Group items by strategic themes' },
];

function StrategyHub({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('goals');
  const [goals, setGoals] = useState([]);
  const [okrs, setOKRs] = useState([]);
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [goalsData, okrsData, themesData] = await Promise.all([
        api.getGoals(),
        api.getOKRs(),
        api.getThemes(),
      ]);
      setGoals(goalsData);
      setOKRs(okrsData);
      setThemes(themesData);
    } catch (error) {
      console.error('Failed to load strategy data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGoal = async (id) => {
    if (!window.confirm('Delete this goal?')) return;
    try { await api.deleteGoal(id); setGoals(goals.filter(g => g._id !== id)); } catch (e) { console.error(e); }
  };

  const handleDeleteOKR = async (id) => {
    if (!window.confirm('Delete this OKR?')) return;
    try { await api.deleteOKR(id); setOKRs(okrs.filter(o => o._id !== id)); } catch (e) { console.error(e); }
  };

  const handleDeleteTheme = async (id) => {
    if (!window.confirm('Delete this theme?')) return;
    try { await api.deleteTheme(id); setThemes(themes.filter(t => t._id !== id)); } catch (e) { console.error(e); }
  };

  const currentData = activeTab === 'goals' ? goals : activeTab === 'okrs' ? okrs : themes;

  return (
    <div className="min-h-full" data-testid="strategy-hub">
      {/* Breadcrumbs */}
      <div className="px-6 lg:px-8 pt-6 pb-2">
        <Breadcrumbs items={[
          { label: 'Dashboard', path: '/' },
          { label: 'Strategy Hub', path: '/strategy' },
        ]} />
      </div>
      {/* Page header */}
      <div className="px-6 lg:px-8 pt-4 pb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Strategy Hub</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage goals, OKRs, and themes that drive your roadmap</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-5 py-2.5 bg-primary text-primary-foreground rounded-xl hover:opacity-90 font-semibold flex items-center gap-2 shadow-sm shadow-primary/20 btn-press transition-opacity"
            data-testid="create-strategy-button"
          >
            <Plus className="h-4 w-4" /> Add {TAB_CONFIG.find(t => t.key === activeTab)?.label.slice(0, -1)}
          </button>
        </div>
      </div>

      <div className="px-6 lg:px-8 pb-8">
        {/* Tabs */}
        <div className="flex gap-3 mb-8 border-b border-border">
          {TAB_CONFIG.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-6 py-3 font-medium border-b-2 transition-all flex items-center gap-2 ${
                activeTab === tab.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              <span className="ml-1 text-xs bg-muted px-2 py-0.5 rounded-full">
                {tab.key === 'goals' ? goals.length : tab.key === 'okrs' ? okrs.length : themes.length}
              </span>
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => <div key={i} className="h-40 bg-muted rounded-xl animate-pulse" />)}
          </div>
        ) : currentData.length === 0 ? (
          <div className="text-center py-16 bg-card rounded-xl border border-border">
            {React.createElement(TAB_CONFIG.find(t => t.key === activeTab)?.icon, { className: 'h-12 w-12 mx-auto text-muted-foreground mb-4' })}
            <h3 className="text-lg font-semibold mb-2">No {TAB_CONFIG.find(t => t.key === activeTab)?.label} Yet</h3>
            <p className="text-muted-foreground mb-6">{TAB_CONFIG.find(t => t.key === activeTab)?.description}</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 font-medium inline-flex items-center gap-2"
            >
              <Plus className="h-4 w-4" /> Create First {TAB_CONFIG.find(t => t.key === activeTab)?.label.slice(0, -1)}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeTab === 'goals' && goals.map(goal => (
              <GoalCard key={goal._id} goal={goal} onEdit={() => setEditingItem(goal)} onDelete={() => handleDeleteGoal(goal._id)} />
            ))}
            {activeTab === 'okrs' && okrs.map(okr => (
              <OKRCard key={okr._id} okr={okr} goals={goals} onEdit={() => setEditingItem(okr)} onDelete={() => handleDeleteOKR(okr._id)} />
            ))}
            {activeTab === 'themes' && themes.map(theme => (
              <ThemeCard key={theme._id} theme={theme} onDelete={() => handleDeleteTheme(theme._id)} />
            ))}
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateStrategyModal
          type={activeTab}
          goals={goals}
          onClose={() => setShowCreateModal(false)}
          onCreated={(newItem) => {
            if (activeTab === 'goals') setGoals([...goals, newItem]);
            else if (activeTab === 'okrs') setOKRs([...okrs, newItem]);
            else setThemes([...themes, newItem]);
            setShowCreateModal(false);
          }}
        />
      )}
    </div>
  );
}

function GoalCard({ goal, onEdit, onDelete }) {
  return (
    <div className="group bg-card border border-border rounded-xl p-5 hover:shadow-lg transition-all duration-200 hover:-translate-y-1" data-testid={`goal-card-${goal._id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Target className="h-5 w-5 text-primary" />
          <h3 className="font-semibold text-foreground">{goal.title}</h3>
        </div>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={onDelete} className="p-1.5 hover:bg-destructive/10 text-destructive rounded-md transition-colors" data-testid={`delete-goal-${goal._id}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      {goal.description && <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{goal.description}</p>}
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span className={`px-2 py-1 rounded-md font-medium ${
          goal.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
          goal.status === 'completed' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
          'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
        }`}>{goal.status}</span>
        {goal.target_date && (
          <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(goal.target_date).toLocaleDateString()}</span>
        )}
        {goal.owner && (
          <span className="flex items-center gap-1"><User className="h-3 w-3" />{goal.owner.split('@')[0]}</span>
        )}
      </div>
      {goal.progress > 0 && (
        <div className="mt-3">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-medium">{Math.round(goal.progress)}%</span>
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary transition-all duration-300" style={{ width: `${goal.progress}%` }} />
          </div>
        </div>
      )}
    </div>
  );
}

function OKRCard({ okr, goals, onEdit, onDelete }) {
  const linkedGoal = goals.find(g => g._id === okr.goal_id);
  return (
    <div className="group bg-card border border-border rounded-xl p-5 hover:shadow-lg transition-all duration-200 hover:-translate-y-1" data-testid={`okr-card-${okr._id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Flag className="h-5 w-5 text-amber-500" />
          <h3 className="font-semibold text-foreground">{okr.title}</h3>
        </div>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={onDelete} className="p-1.5 hover:bg-destructive/10 text-destructive rounded-md transition-colors" data-testid={`delete-okr-${okr._id}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      {okr.description && <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{okr.description}</p>}
      {linkedGoal && (
        <div className="mb-3 text-xs px-2 py-1 bg-primary/10 text-primary rounded-md inline-flex items-center gap-1">
          <Target className="h-3 w-3" /> {linkedGoal.title}
        </div>
      )}
      {okr.key_results && okr.key_results.length > 0 && (
        <div className="space-y-1 mb-3">
          {okr.key_results.map((kr, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <div className="w-4 h-4 rounded-full border border-border flex items-center justify-center text-muted-foreground">{i + 1}</div>
              <span className="text-muted-foreground">{kr}</span>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span className={`px-2 py-1 rounded-md font-medium ${
          okr.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
          'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
        }`}>{okr.status}</span>
      </div>
    </div>
  );
}

function ThemeCard({ theme, onDelete }) {
  return (
    <div className="group bg-card border border-border rounded-xl p-5 hover:shadow-lg transition-all duration-200 hover:-translate-y-1" data-testid={`theme-card-${theme._id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 rounded-md" style={{ backgroundColor: theme.color }} />
          <h3 className="font-semibold text-foreground">{theme.name}</h3>
        </div>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={onDelete} className="p-1.5 hover:bg-destructive/10 text-destructive rounded-md transition-colors" data-testid={`delete-theme-${theme._id}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      {theme.description && <p className="text-sm text-muted-foreground line-clamp-2">{theme.description}</p>}
    </div>
  );
}

function CreateStrategyModal({ type, goals, onClose, onCreated }) {
  const [formData, setFormData] = useState(
    type === 'goals' ? { title: '', description: '', target_date: '', owner: '' } :
    type === 'okrs' ? { title: '', description: '', goal_id: '', key_results: [''], owner: '' } :
    { name: '', description: '', color: '#82d230' }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let result;
      if (type === 'goals') {
        result = await api.createGoal(formData);
      } else if (type === 'okrs') {
        const cleanData = { ...formData, key_results: formData.key_results.filter(kr => kr.trim()) };
        result = await api.createOKR(cleanData);
      } else {
        result = await api.createTheme(formData);
      }
      onCreated(result);
    } catch (error) {
      console.error('Failed to create:', error);
      alert('Failed to create. Please try again.');
    }
  };

  const addKeyResult = () => setFormData({ ...formData, key_results: [...formData.key_results, ''] });
  const updateKeyResult = (index, value) => {
    const updated = [...formData.key_results];
    updated[index] = value;
    setFormData({ ...formData, key_results: updated });
  };
  const removeKeyResult = (index) => {
    const updated = formData.key_results.filter((_, i) => i !== index);
    setFormData({ ...formData, key_results: updated });
  };

  const labels = { goals: 'Goal', okrs: 'OKR', themes: 'Theme' };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="create-strategy-modal">
      <div className="bg-card rounded-xl max-w-lg w-full border border-border shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-border">
          <h2 className="text-xl font-bold">Create New {labels[type]}</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">{type === 'themes' ? 'Name' : 'Title'} *</label>
            <input
              type="text"
              value={type === 'themes' ? formData.name : formData.title}
              onChange={(e) => setFormData({ ...formData, [type === 'themes' ? 'name' : 'title']: e.target.value })}
              required
              className="w-full px-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="strategy-title-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="strategy-description-input"
            />
          </div>

          {type === 'goals' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Target Date</label>
                  <input type="date" value={formData.target_date} onChange={(e) => setFormData({ ...formData, target_date: e.target.value })} className="w-full px-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary" data-testid="goal-target-date" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Owner</label>
                  <input type="text" value={formData.owner} onChange={(e) => setFormData({ ...formData, owner: e.target.value })} placeholder="email@example.com" className="w-full px-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary" data-testid="goal-owner" />
                </div>
              </div>
            </>
          )}

          {type === 'okrs' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Linked Goal</label>
                <select value={formData.goal_id} onChange={(e) => setFormData({ ...formData, goal_id: e.target.value })} className="w-full px-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary" data-testid="okr-goal-select">
                  <option value="">None</option>
                  {goals.map(g => <option key={g._id} value={g._id}>{g.title}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Key Results</label>
                <div className="space-y-2">
                  {formData.key_results.map((kr, i) => (
                    <div key={i} className="flex gap-2">
                      <input type="text" value={kr} onChange={(e) => updateKeyResult(i, e.target.value)} placeholder={`Key Result ${i + 1}`} className="flex-1 px-4 py-2 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm" data-testid={`kr-input-${i}`} />
                      {formData.key_results.length > 1 && (
                        <button type="button" onClick={() => removeKeyResult(i)} className="p-2 text-destructive hover:bg-destructive/10 rounded-lg transition-colors">
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={addKeyResult} className="text-sm text-primary hover:text-primary/80 font-medium flex items-center gap-1">
                    <Plus className="h-3 w-3" /> Add Key Result
                  </button>
                </div>
              </div>
            </>
          )}

          {type === 'themes' && (
            <div>
              <label className="block text-sm font-medium mb-2">Color</label>
              <div className="flex gap-3 items-center">
                <input type="color" value={formData.color} onChange={(e) => setFormData({ ...formData, color: e.target.value })} className="w-16 h-12 border border-input rounded-lg cursor-pointer" data-testid="theme-color" />
                <code className="text-sm font-mono bg-muted px-3 py-2 rounded-lg">{formData.color}</code>
              </div>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-4">
            <button type="button" onClick={onClose} className="px-5 py-2.5 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 font-medium" data-testid="cancel-strategy">
              Cancel
            </button>
            <button type="submit" className="px-5 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 font-medium shadow-sm" data-testid="submit-strategy">
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default StrategyHub;
